import React, { useEffect, useState, useContext } from "react";
import { auth, db } from "../../utils/firebase";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { AuthContext } from "../../utils/AuthContext";
import DarkModeToggle from "../../components/token/DarkModeToggle";
import Footer from "../../components/feature/Footer";

export default function Settings() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const { currentUser, signOut } = useContext(AuthContext);

  // Fetch user data from Firestore when component mounts
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      setSuccessMsg("");

      // const currentUser = auth.currentUser;
      if (!currentUser) {
        setError("No logged in user found. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, "users", currentUser.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const userData = docSnap.data();
          // Populate local states with user data
          setUsername(userData.username || "");
          setEmail(userData.email || currentUser.email || "");
          setDarkMode(userData.darkMode || false);
        } else {
          let userData = {
            username: currentUser.displayName || "",
            email: currentUser.email || "",
          };
          await setDoc(doc(db, "users", currentUser.uid), userData);
          setError("No user profile found in Firestore.");
          fetchProfile();
        }
      } catch (err) {
        setError("Failed to load user data: " + err.message);
      }

      setLoading(false);
    };

    fetchProfile();
  }, [currentUser]);

  // Save changes to Firestore
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg("");

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError("No logged in user found.");
      return;
    }

    try {
      const docRef = doc(db, "users", currentUser.uid);
      await updateDoc(docRef, {
        username,
        email,
        darkMode,
      });
      setSuccessMsg("Profile updated successfully!");
    } catch (err) {
      setError("Failed to update profile: " + err.message);
    }
  };

  // Render loading, errors, or the form
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p>Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="bg-white p-4 rounded-md shadow">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 text-gray-800 md:px-20 px-8">
      <main className="flex-1 flex items-center justify-center px-4 py-6">
        <div className="w-full max-w-md bg-white rounded-md shadow p-6">
          <h1 className="text-2xl font-bold mb-4">Profile</h1>

          {/* Success message */}
          {successMsg && (
            <div className="text-green-600 mb-2">{successMsg}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Username
              </label>
              <input
                type="text"
                id="username"
                placeholder="Enter username"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                placeholder="Enter email"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <DarkModeToggle isDarkMode={darkMode} setIsDarkMode={setDarkMode} />

            {/* Save button */}
            <button
              type="submit"
              className="w-full bg-blue-600 text-white text-sm font-semibold py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Save Changes
            </button>
          </form>

          {/* Disconnect button */}
          <button
            onClick={signOut}
            className="w-full mt-4 bg-red-600 text-white text-sm font-semibold py-2 rounded-md hover:bg-red-700 transition-colors"
          >
            Disconnect
          </button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
